import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { ProgramCode } from '@app/programs/programs.types'
import { customerBaseQuery, tagTypes } from './utils'
import { ContentPageType } from './customer.types'

export interface GetAddressListResponse {
  text: string
  description: string
  type: 'Address' | 'Street' | 'Locality' | 'District' | 'Container'
  id: string
}

export interface GetAddressListArgs {
  /** Address to search for */
  query: string
  /** Loqate-specifc 'container-id' used to unpack streets into addresses */
  containerId?: string
}

export interface GetAddressDetailsResponse {
  address: string
  postal_code: string
  city: string
  country: string
  country_name: string
}

export interface PostOrderResponse {
  order_id: string
}
export interface PostUserPayload {
  name: string
  email: string
}

export interface PostUserResponse {
  id: string
  name: string
  email: string
}

export interface PostOrderPayload {
  user_id: string
  promo_code: ProgramCode
  order_item: {
    /**
     * The model of the new product
     */
    product_model: string
    /**
     * The serial number of the new product
     */
    serial_number: string
    /**
     * The date the new product was purchased
     */
    purchased_date: string
    /**
     * The store where the new product was purchased
     */
    purchased_store: string
    /**
     * The manufacturer of the traded product
     */
    manufacturer: string
    /**
     * Whether the traded product is in working condition
     */
    working_condition: boolean
    /**
     * The serial number of the traded product
     */
    traded_serial: string
    /**
     * The offer SKU of the traded product e.g. BRAND_PREMIUM
     */
    traded_sku: string
  }
  payment_details: {
    /**
     * The full name of the account holder
     */
    name: string
    /**
     * The IBAN of the account holder
     */
    iban: string
  }
  shipping_details: {
    /**
     * The full name of the recipient
     */
    name: string
    /**
     * The street address of the recipient
     */
    address: string
    /**
     * The postal code of the recipient
     */
    postal_code: string
    /**
     * The city of the recipient
     */
    city: string
    /**
     * The country code of the recipient
     */
    country: string
  }
}

export interface GetUploadUrlResponse {
  url: string
}

export interface GetUploadUrlArgs {
  userId: string
  type: string
}

export interface GetCampaignDetailsArgs {
  programme_slug: string
  campaign_code: string
}

export interface GetCampaignContentArgs extends GetCampaignDetailsArgs {
  page_type: ContentPageType
}

export interface GetCampaignDetailsResponse {
  campaign: {
    programme_id: string
    code: string
    name: string
    start_date: string
    end_date: string
    timezone: string
    language_config: {
      default: string
      available: string[]
    }
    status: string
  }
}

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: fetchBaseQuery(customerBaseQuery),
  tagTypes,
  endpoints: builder => ({
    // Get presigned URLs
    getUploadUrl: builder.query<GetUploadUrlResponse, GetUploadUrlArgs>({
      query: ({ userId, type }) => `/v1/users/${userId}/upload-url?type=${type}`,
    }),
    // Get list of addresses from Loqate
    getAddressList: builder.query<GetAddressListResponse[], GetAddressListArgs>({
      query: ({ query, containerId }) => {
        if (containerId) {
          return `/v1/addresses/find?address=${query}&container=${encodeURI(containerId)}`
        }
        return `/v1/addresses/find?address=${query}`
      },
    }),
    // Get specific address from Loqate
    getAddressDetails: builder.query<GetAddressDetailsResponse, string>({
      query: addressId => `/v1/addresses/${encodeURI(addressId)}/retrieve`,
    }),
    // Create a new user
    postUser: builder.mutation<PostUserResponse, PostUserPayload>({
      query: payload => ({
        url: '/v1/users',
        method: 'POST',
        body: payload,
      }),
    }),
    // Upload file to S3
    putFile: builder.mutation<void, { url: string; file: File; type: string }>({
      query: ({ url, file, type }) => ({
        url,
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'binary/octet-stream',
          // Metadata header
          'x-amz-meta-type': type,
          /** Explicitly add the Origin header to avoid CORS errors when writing to S3 */
          Origin: window.location.origin,
        },
      }),
    }),
    // Create a new order
    postOrder: builder.mutation<PostOrderResponse, PostOrderPayload>({
      query: payload => ({
        url: '/v1/orders',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useGetUploadUrlQuery,
  useLazyGetUploadUrlQuery,
  useGetAddressListQuery,
  useGetAddressDetailsQuery,
  usePostUserMutation,
  usePutFileMutation,
  usePostOrderMutation,
} = customerApi
